import { useEffect, useRef, useState } from 'react';
import { addError } from '@infra/datadog/tracking';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { PieceNameLower, SquareName } from '@libs/chess/types';
import { deadline } from '@libs/deadline';
import { ApiError } from '@libs/errors';
import { SocketInternalEvents } from '@libs/socket-signalr';

import { IGame } from '@features/game/types/game';
import { gameStructuralSharing } from '@features/game/utils/gameStructuralSharing';
import { COMMON_QUERY_KEYS } from '@features/shared/api';

import {
  components,
  GameSocket,
  GET,
  POST,
  GameSocketEvents,
  RoundSocketEvents,
  RoundSocket,
} from './clients';

/*
    ____,-------------------------------,____
    \   |             REST              |   /
    /___|-------------------------------|___\
*/

export function useGameQuery(
  id: string,
  {
    enabled = true,
    staleTime,
    customAccessToken,
  }: { enabled?: boolean; staleTime?: number; customAccessToken?: string } = {},
) {
  return useQuery({
    enabled,
    refetchOnWindowFocus: false,
    staleTime,
    queryKey: COMMON_QUERY_KEYS.game(id),
    queryFn: async () => {
      return (
        await GET('/api/v1/games/{gameId}', {
          params: { path: { gameId: id } },
          headers: customAccessToken ? { Authorization: `Bearer ${customAccessToken}` } : undefined,
        })
      ).data! as IGame;
    },
    structuralSharing: gameStructuralSharing,
  });
}

export function useGameMoveV2Mutation({ customAccessToken }: { customAccessToken?: string } = {}) {
  return useMutation({
    retry(failureCount, error) {
      // sic! обрабатываем только ошибки, связанные с проблемами сети (и дедлайнами)
      if (!(error instanceof ApiError) || !error.isFetchError) {
        return false;
      }

      if (failureCount === 4) return false;

      const customError = new Error('Retrying move', { cause: error });
      customError.name = 'MoveRetryError';
      addError(customError);

      return true;
    },
    retryDelay: 50 /* ms */,
    mutationFn: async ({
      id,
      move,
      deadlineMs,
    }: {
      id: string;
      move: {
        from: SquareName;
        to: SquareName;
        promoteTo?: PieceNameLower;
        currentFen: string;
        timings: components['schemas']['MoveTimings'];
      };
      deadlineMs: number;
    }) => {
      const promise = deadline(deadlineMs, (signal) =>
        POST('/api/v2/games/{gameId}/move', {
          params: { path: { gameId: id } },
          body: move,
          signal,
          headers: customAccessToken ? { Authorization: `Bearer ${customAccessToken}` } : undefined,
        }),
      );

      return (await promise).data!;
    },
  });
}

export function useOfferDrawMutation({ customAccessToken }: { customAccessToken?: string } = {}) {
  return useMutation({
    mutationFn: async (gameId: string) => {
      return (
        await POST('/api/v1/games/{gameId}/request-draw', {
          params: { path: { gameId } },
          headers: customAccessToken ? { Authorization: `Bearer ${customAccessToken}` } : undefined,
        })
      ).data!;
    },
  });
}

export function useAcceptDrawMutation({ customAccessToken }: { customAccessToken?: string } = {}) {
  return useMutation({
    mutationFn: async (gameId: string) => {
      return (
        await POST('/api/v1/games/{gameId}/accept-draw', {
          params: { path: { gameId } },
          headers: customAccessToken ? { Authorization: `Bearer ${customAccessToken}` } : undefined,
        })
      ).data!;
    },
  });
}

export function useDeclineDrawMutation({ customAccessToken }: { customAccessToken?: string } = {}) {
  return useMutation({
    mutationFn: async (gameId: string) => {
      return (
        await POST('/api/v1/games/{gameId}/decline-draw', {
          params: { path: { gameId } },
          headers: customAccessToken ? { Authorization: `Bearer ${customAccessToken}` } : undefined,
        })
      ).data!;
    },
  });
}

export function useResignMutation({ customAccessToken }: { customAccessToken?: string } = {}) {
  return useMutation({
    mutationFn: async (gameId: string) => {
      return (
        await POST('/api/v1/games/{gameId}/surrender', {
          params: { path: { gameId } },
          headers: customAccessToken ? { Authorization: `Bearer ${customAccessToken}` } : undefined,
        })
      ).data!;
    },
  });
}

export function useClaimVictoryMutation({
  customAccessToken,
}: { customAccessToken?: string } = {}) {
  return useMutation({
    mutationFn: async (gameId: string) => {
      return (
        await POST('/api/v1/games/{gameId}/claim-victory', {
          params: { path: { gameId } },
          headers: customAccessToken ? { Authorization: `Bearer ${customAccessToken}` } : undefined,
        })
      ).data!;
    },
  });
}

/*
    ____,-------------------------------,____
    \   |             Socket            |   /
    /___|-------------------------------|___\
*/

// ------------------------------------------
// Game

export function useGameSocket({
  gameId,
  customSocketUrl,
  customAccessToken,
  enabled = true,
  cb,
}: {
  gameId: string;
  customSocketUrl?: string;
  customAccessToken?: string;
  enabled?: boolean;
  cb: (e: GameSocketEvents | SocketInternalEvents) => void;
}) {
  const refCb = useRef(cb);
  refCb.current = cb;

  const createSocket = () => {
    if (!enabled) return null;

    const s = new GameSocket(gameId, customSocketUrl, customAccessToken);
    s.subscribe((e) => {
      refCb.current(e);

      // отключить сокет, если игра закончена
      if (e.eventType === 'GameChanged' && e.currentState.status === 'Finished') {
        s.discard();
      }
    });

    return s;
  };

  // FIXME: избавиться от синхронного setState
  const [socket, setSocket] = useState(createSocket);
  if (enabled && (!socket || socket?.gameId !== gameId)) {
    socket?.discard();
    setSocket(createSocket());
  } else if (!enabled && socket) {
    socket.discard();
    setSocket(null);
  }

  useEffect(
    () => () => {
      if (socket) socket.discard();
    },
    [socket],
  );
}

// ------------------------------------------
// Round

type RoundWsContext = {
  games?: Record<string, components['schemas']['GameDto']>;
};

const globalRoundWs: Record<string, TournamentGamesSotre> = {};

export function useRoundSocket({
  tournamentId,
  roundNumber,
  cb,
  customSocketUrl,
  enabled = true,
}: {
  tournamentId: string;
  roundNumber: number;
  cb: (e: RoundSocketEvents | SocketInternalEvents, ctx: RoundWsContext) => void;
  customSocketUrl?: string;
  enabled?: boolean;
}) {
  const queryClient = useQueryClient();

  const ref = useRef(cb);
  ref.current = cb;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const key = `${tournamentId}-|-${roundNumber}-|-${customSocketUrl}`;

    if (!globalRoundWs[key]) {
      globalRoundWs[key] = new TournamentGamesSotre(
        new RoundSocket(tournamentId, roundNumber, customSocketUrl),
        queryClient,
      );
    }

    const store = globalRoundWs[key];
    const unsubscribe = store.ws.subscribe((e) => {
      ref.current(e, { games: store.games });
    });

    // отправляем снэпшот
    const snapshot = store.snapshot;
    if (snapshot) {
      ref.current(snapshot, { games: store.games });
    }

    return () => {
      unsubscribe();
      if (store.subscribers === 0) {
        store.discard();
        delete globalRoundWs[key];
      }
    };
  }, [tournamentId, roundNumber, enabled, customSocketUrl, queryClient]);
}

// Стор нужен, чтобы хранить и обновлять snapshot
class TournamentGamesSotre {
  games: Record<string, components['schemas']['GameDto']> | undefined;
  snapshot: components['schemas']['RoundGamesState'] | undefined;
  ws: RoundSocket;
  #ql: QueryClient;
  #roundId: string | undefined;

  constructor(ws: RoundSocket, ql: QueryClient) {
    this.ws = ws;
    this.#ql = ql;
    ws.subscribe((e) => {
      switch (e.eventType) {
        case 'RoundGamesState':
          this.games = {};

          e.data.forEach((g) => {
            if (!this.#roundId && g.round?.roundId) {
              this.#roundId = g.round?.roundId;
            }

            this.games![g.id] = g;
            // кэш игры
            this.#ql.setQueryData(COMMON_QUERY_KEYS.game(g.id), g);
          });

          // кэш раунда
          if (this.#roundId) {
            this.#ql.setQueryData(
              COMMON_QUERY_KEYS.roundGames(this.ws.tournamentId, this.#roundId),
              e.data,
            );
          }
          this.#update();
          break;
        case 'GameChanged':
        case 'DrawRequest':
        case 'DrawAccept':
        case 'DrawDecline':
        case 'Surrender':
          if (!this.#roundId && e.currentState.round?.roundId) {
            this.#roundId = e.currentState.round.roundId;
          }

          // события могут прийти раньше снэпшота, не теряем их
          this.games ??= {};
          this.games[e.gameId] = e.currentState;
          // кэш игры
          this.#ql.setQueryData(COMMON_QUERY_KEYS.game(e.gameId), e.currentState);

          // кэш раунда
          if (this.#roundId) {
            this.#ql.setQueryData(
              COMMON_QUERY_KEYS.roundGames(this.ws.tournamentId, this.#roundId),
              Object.values(this.games),
            );
          }
          this.#update();
          break;
      }

      // SIC! Нельзя отключать сокет по завершении всех игр, т.к. есть турниры,
      // в которые игры добавляются динамически
    });
  }

  #update(): components['schemas']['RoundGamesState'] | undefined {
    if (!this.games) return;

    this.snapshot = {
      timestampMs: Date.now(),
      eventType: 'RoundGamesState',
      data: Object.values(this.games),
      roundNumber: this.ws.roundNumber.toString(),
      tournamentId: this.ws.tournamentId,
    };
  }

  get subscribers(): number {
    return Math.max(this.ws.subscribers - 1, 0);
  }

  discard() {
    this.ws.discard();
  }
}
